import React, { useRef, useEffect } from 'react';
import { Lock, Shield, Key, Check, ChevronRight, Users, Package, AlertTriangle } from 'lucide-react';
import { useTheme } from './ThemeContext';
import ThemeToggle from './components/ThemeToggle';
import ComingSoonDialog from './components/ComingSoonDialog';

const RPFEncryptWebsite = () => {
  const [activeSection, setActiveSection] = React.useState('home');
  const [openFaq, setOpenFaq] = React.useState(null);
  const [showComingSoon, setShowComingSoon] = React.useState(false);
  const { theme } = useTheme();
  
  // Create refs for each section
  const homeRef = useRef(null);
  const featuresRef = useRef(null);
  const pricingRef = useRef(null);
  const faqRef = useRef(null);

  // Function to handle smooth scrolling to sections
  const scrollToSection = (sectionRef, sectionName) => {
    window.scrollTo({
      top: sectionRef.current.offsetTop - 80, // Offset for the navbar height
      behavior: 'smooth'
    });
    setActiveSection(sectionName);
  };

  // Function to open the coming soon dialog
  const handleGetStarted = () => {
    setShowComingSoon(true);
  };

  const handleDashboardClicked = () => {
    window.location.href = "https://panel.rpfprotector.com";
  };

  // Update active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Add some offset
      
      // Check which section is in view
      if (homeRef.current && scrollPosition < featuresRef.current.offsetTop) {
        setActiveSection('home');
      } else if (featuresRef.current && scrollPosition < pricingRef.current.offsetTop) {
        setActiveSection('features');
      } else if (pricingRef.current && scrollPosition < faqRef.current.offsetTop) {
        setActiveSection('pricing');
      } else if (faqRef.current) {
        setActiveSection('faq');
      }
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'} flex flex-col transition-colors duration-300`}>
      {/* Coming Soon Dialog */}
      <ComingSoonDialog 
        isOpen={showComingSoon} 
        onClose={() => setShowComingSoon(false)} 
      />
      
      {/* Fixed Navigation */}
      <nav className={`${theme === 'dark' ? 'bg-gray-800 shadow-gray-900/50' : 'bg-white shadow-sm'} py-4 px-6 sticky top-0 z-10 transition-colors duration-300`}>
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Lock className="text-indigo-600 dark:text-indigo-400" size={28} />
            <span className="text-xl font-bold">RPFProtect</span>
          </div>
          
          <div className="hidden md:flex space-x-8">
            <button 
              onClick={() => scrollToSection(homeRef, 'home')}
              className={`font-medium transition-colors duration-300 ${activeSection === 'home' 
                ? 'text-indigo-600 dark:text-indigo-400' 
                : `${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}`}
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection(featuresRef, 'features')}
              className={`font-medium transition-colors duration-300 ${activeSection === 'features' 
                ? 'text-indigo-600 dark:text-indigo-400' 
                : `${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}`}
            >
              Features
            </button>
            <button 
              onClick={() => scrollToSection(pricingRef, 'pricing')}
              className={`font-medium transition-colors duration-300 ${activeSection === 'pricing' 
                ? 'text-indigo-600 dark:text-indigo-400' 
                : `${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}`}
            >
              Pricing
            </button>
            <button 
              onClick={() => scrollToSection(faqRef, 'faq')}
              className={`font-medium transition-colors duration-300 ${activeSection === 'faq' 
                ? 'text-indigo-600 dark:text-indigo-400' 
                : `${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}`}
            >
              FAQ
            </button>
          </div>
          
          <div className="flex items-center space-x-3">
            <ThemeToggle />
            <button 
              onClick={handleDashboardClicked}
              className="bg-indigo-600 dark:bg-indigo-500 text-white px-4 py-2 rounded-md font-medium hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-all"
            >
              Dashboard
            </button>
          </div>
        </div>
      </nav>

      {/* Home Section */}
      <section ref={homeRef} id="home" className="scroll-mt-20">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-indigo-600 to-purple-700 dark:from-indigo-700 dark:to-purple-900 text-white py-20 px-6 transition-colors duration-300">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6">Secure Your FiveM RPF Mods with RPFProtect</h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">Protect your valuable FiveM mods from unauthorized sharing with our powerful encryption solution.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button 
                onClick={() => scrollToSection(pricingRef, 'pricing')}
                className="bg-white text-indigo-600 dark:bg-gray-800 dark:text-indigo-400 px-8 py-3 rounded-md font-bold text-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
              >
                Get Started
              </button>
              <button 
                onClick={() => window.location.href = '/download/RpfProtect.asi'} 
                className="bg-indigo-500 text-white dark:bg-indigo-600 px-8 py-3 rounded-md font-bold text-lg hover:bg-indigo-600 dark:hover:bg-indigo-700 transition-all"
              >
                Download Plugin
              </button>
            </div>
          </div>
        </div>

        {/* Main Features */}
        <div className={`py-16 px-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} transition-colors duration-300`}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Why Choose RPFProtect?</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'} p-6 rounded-lg transition-colors duration-300`}>
                <div className="bg-indigo-100 dark:bg-indigo-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 transition-colors duration-300">
                  <Lock className="text-indigo-600 dark:text-indigo-400" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-3">Encrypted using modern, high-security standards</h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Secure your RPF files with advanced encryption that prevents unauthorized access and sharing.</p>
              </div>
              
              <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'} p-6 rounded-lg transition-colors duration-300`}>
                <div className="bg-indigo-100 dark:bg-indigo-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 transition-colors duration-300">
                  <Key className="text-indigo-600 dark:text-indigo-400" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-3">Hardware-Locked Files</h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Protected files are bound to the user's hardware ID, ensuring your mods only work on authorized devices.</p>
              </div>
              
              <div className={`${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'} p-6 rounded-lg transition-colors duration-300`}>
                <div className="bg-indigo-100 dark:bg-indigo-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 transition-colors duration-300">
                  <Users className="text-indigo-600 dark:text-indigo-400" size={24} />
                </div>
                <h3 className="text-xl font-bold mb-3">Complete Control</h3>
                <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Generate and manage your encrypted assets for your customers while maintaining full control over your content.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className={`py-16 px-6 ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'} transition-colors duration-300`}>
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Secure Your FiveM Mods?</h2>
            <p className={`text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-8 transition-colors duration-300`}>Join other mod creators who trust RPFProtect to protect their valuable work.</p>
            <button 
              onClick={() => scrollToSection(pricingRef, 'pricing')}
              className="bg-indigo-600 dark:bg-indigo-500 text-white px-8 py-3 rounded-md font-bold text-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-all"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} id="features" className={`py-16 px-6 scroll-mt-20 ${theme === 'dark' ? 'bg-gray-800' : ''} transition-colors duration-300`}>
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold text-center mb-16">Powerful Features</h1>
          
          <div className="grid md:grid-cols-2 gap-16">
            <div className="transform transition-all duration-500 hover:scale-105">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <Shield className="text-indigo-600 dark:text-indigo-400 mr-3 transition-colors duration-300" size={28} />
                Advanced Security
              </h2>
              <ul className="space-y-4">
                <li className="flex">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <div>
                    <p className="font-medium">Secure Encryption Algorithm</p>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Specially designed for FiveM RPF files to ensure maximum protection and compatibility.</p>
                  </div>
                </li>
                <li className="flex">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <div>
                    <p className="font-medium">Hardware ID Binding</p>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Protected files are locked to the specific device the moment they are first loaded.</p>
                  </div>
                </li>
                <li className="flex">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <div>
                    <p className="font-medium">Tamper-Proof</p>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Encrypted files cannot be modified or reverse-engineered.</p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="transform transition-all duration-500 hover:scale-105">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <Key className="text-indigo-600 dark:text-indigo-400 mr-3 transition-colors duration-300" size={28} />
                Access Management
              </h2>
              <ul className="space-y-4">
                <li className="flex">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <div>
                    <p className="font-medium">Flexible License Generation</p>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Generate up to 30 licenses per RPF file at no additional cost.</p>
                  </div>
                </li>
                <li className="flex">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <div>
                    <p className="font-medium">License Tracking</p>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Monitor which licenses have been activated and on which devices.</p>
                  </div>
                </li>
                <li className="flex">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <div>
                    <p className="font-medium">License Revocation</p>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Ability to revoke access if unauthorized use is detected.</p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="transform transition-all duration-500 hover:scale-105">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <Package className="text-indigo-600 dark:text-indigo-400 mr-3 transition-colors duration-300" size={28} />
                Compatibility
              </h2>
              <ul className="space-y-4">
                <li className="flex">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <div>
                    <p className="font-medium">NVES Support</p>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Full compatibility with NVES and other visual enhancement mods like weapon skins.</p>
                  </div>
                </li>
                <li className="flex">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <div>
                    <p className="font-medium">All RPF Types</p>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Works with all types of RPF files that are loaded from the FiveM mods directory.</p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="transform transition-all duration-500 hover:scale-105">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <Users className="text-indigo-600 dark:text-indigo-400 mr-3 transition-colors duration-300" size={28} />
                User Experience
              </h2>
              <ul className="space-y-4">
                <li className="flex">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <div>
                    <p className="font-medium">Easy Installation</p>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>Simply put our plugin into FiveM's plugins folder and you're good to go.</p>
                  </div>
                </li>
                <li className="flex">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <div>
                    <p className="font-medium">Transparent Operation</p>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>The plugin runs silently in the background.</p>
                  </div>
                </li>
                <li className="flex">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <div>
                    <p className="font-medium">No Performance Impact</p>
                    <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>The plugin doesn't affect the game's performance at all, since it only runs once, when FiveM is started.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section ref={pricingRef} id="pricing" className={`py-16 px-6 ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'} scroll-mt-20 transition-colors duration-300`}>
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-center mb-4">Simple, Transparent Pricing</h1>
          <p className={`text-xl text-center ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-16 transition-colors duration-300`}>Get the protection your valuable mods deserve at an affordable price.</p>
          
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg overflow-hidden transform transition-all duration-500 hover:shadow-xl`}>
            <div className={`p-8 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} border-b transition-colors duration-300`}>
              <h2 className="text-2xl font-bold mb-4">Standard Package</h2>
              <div className="flex items-baseline mb-6">
                <span className="text-5xl font-extrabold">€30</span>
                <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} ml-2 transition-colors duration-300`}>paid monthly</span>
              </div>
              <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-6 transition-colors duration-300`}>Everything you need to secure and distribute your FiveM RPF mods.</p>
              <button 
                onClick={handleDashboardClicked}
                className="w-full bg-indigo-600 dark:bg-indigo-500 text-white py-3 rounded-md font-medium hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-all"
              >
                Purchase
              </button>
            </div>
            
            <div className={`p-8 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'} transition-colors duration-300`}>
              <h3 className="font-bold text-lg mb-4">What's included:</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <span>Encrypt up to 15 RPF files</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <span>Generate 30 licenses per RPF file for free</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <span>Key management dashboard</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <span>Easy-to-use management tools</span>
                </li>
                <li className="flex items-start">
                  <Check className="text-green-500 dark:text-green-400 mr-3 flex-shrink-0 mt-1 transition-colors duration-300" size={20} />
                  <span>Fast technical support</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className={`mt-12 ${theme === 'dark' ? 'bg-indigo-900 border-indigo-800' : 'bg-indigo-50 border-indigo-100'} border rounded-lg p-6 transform transition-all duration-500 hover:shadow-lg`}>
            <h3 className="font-bold text-lg mb-4 flex items-center">
              <AlertTriangle className="text-indigo-600 dark:text-indigo-400 mr-3 transition-colors duration-300" size={20} />
              Need more RPF files or keys?
            </h3>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-4 transition-colors duration-300`}>Contact us for custom pricing options if you need to encrypt more than 15 RPF files or require additional licenses.</p>
            <button 
              onClick={handleGetStarted}
              className={`${theme === 'dark' ? 'bg-indigo-900 text-indigo-300 hover:bg-indigo-800' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'} px-4 py-2 rounded-md font-medium transition-all flex items-center`}
            >
              Contact Sales
              <ChevronRight size={16} className="ml-1" />
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqRef} id="faq" className={`py-16 px-6 scroll-mt-20 ${theme === 'dark' ? 'bg-gray-800' : ''} transition-colors duration-300`}>
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-center mb-16">Frequently Asked Questions</h1>
          
          <div className="space-y-4">
            {[
              {
                question: "How does the hardware ID binding work?",
                answer: "When a user uses the generated RPF file for the first time, our system securely captures a unique hardware identifier from their device. The RPF file is then bound to this ID, meaning it will only function on that specific device from that point on."
              },
              {
                question: "What happens if a user changes their hardware?",
                answer: "If a user needs to transfer their activation to a new device, they'll need to contact you, the mod creator. You can then revoke their old license and issue a new one from your allocation of 30 licenses per RPF file."
              },
              {
                question: "Can I see which licenses have been activated?",
                answer: "Yes, our dashboard provides detailed information about which keys have been activated and on which devices, giving you complete visibility and control."
              },
              {
                question: "Is there any performance impact on the encrypted RPF files?",
                answer: "No, our encryption system is designed to have minimal to no impact on the performance of your RPF mods. Users won't notice any difference in gameplay or visual quality since the plugin only runs when FiveM is started."
              },
              {
                question: "What types of RPF files can be encrypted?",
                answer: "RPFProtect works with all types of RPF files that are loaded from the FiveM mods folder, including NVES, weapon skins and other visual enhancement mods. Files directly from the GTAV directory, like audio RPFs are not supported at this time, but we are working on it."
              },
              {
                question: "How do users install encrypted RPF files?",
                answer: "Users install the encrypted files just like they would with regular FiveM mods. The only difference is they need to have our plugin installed in their plugins folder."
              },
              {
                question: "Can I add more RPF files later if needed?",
                answer: "Yes, you can purchase additional capacity if you need to encrypt more than the initial 15 RPF files. Contact our sales team for custom pricing options."
              }
            ].map((faq, index) => (
              <div 
                key={index} 
                className={`border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'} rounded-lg overflow-hidden transform transition-all duration-300 hover:shadow-md`}
              >
                <button 
                  className="w-full text-left p-6 flex justify-between items-center focus:outline-none"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="font-bold text-lg">{faq.question}</span>
                  <ChevronRight 
                    size={20} 
                    className={`transform transition-transform duration-300 ${openFaq === index ? 'rotate-90' : ''}`} 
                  />
                </button>
                <div 
                  className={`px-6 overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96 pb-6 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} transition-colors duration-300`}>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold mb-4">Still have questions?</h2>
            <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-6 transition-colors duration-300`}>We're here to help with any questions you might have about RPFProtect.</p>
            <button 
              onClick={handleGetStarted}
              className="bg-indigo-600 dark:bg-indigo-500 text-white px-6 py-3 rounded-md font-medium hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-all"
            >
              Contact Support
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-800'} text-white py-8 px-6 transition-colors duration-300`}>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Lock size={20} />
              <span className="font-bold">RPFProtect</span>
            </div>
            <div className="flex items-center space-x-4">
              <ThemeToggle className="mr-2" />
              <div className="text-gray-400 text-sm">
                © {new Date().getFullYear()} RPFProtect. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RPFEncryptWebsite;